// DecoratingClassSessionForm.jsx
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import dayjs from "dayjs";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import api from "../../../services/api";

const defaultData = {
  title: "",
  description: "",
  whatYouWillLearn: [""],
  date: "",
  startTime: "",
  endTime: "",
  capacity: "",
  minGroupSize: 5,
};

export default function DecoratingClassSessionForm({
  open,
  onClose,
  onCreated,
}) {
  const [formData, setFormData] = useState(defaultData);
  const [loading, setLoading] = useState(false);
  const snackbar = useSnackbarAlert();

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleLearnChange = (index, value) => {
    const updated = [...formData.whatYouWillLearn];
    updated[index] = value;
    setFormData({ ...formData, whatYouWillLearn: updated });
  };

  const addLearnPoint = () => {
    setFormData({
      ...formData,
      whatYouWillLearn: [...formData.whatYouWillLearn, ""],
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const slotDate = dayjs(formData.date).startOf("day").toISOString();
      const slotTime = `${formData.startTime} – ${formData.endTime}`;

      await api.post("/admin/classes/sessions", {
        ...formData,
        date: new Date(formData.date),
        slotReference: {
          date: slotDate,
          time: slotTime,
        },
      });

      snackbar.success("Session created!");
      onCreated();
      setFormData(defaultData);
    } catch (err) {
      snackbar.error("Failed to create session");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Create a Decorating Class Session
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack spacing={2}>
        <TextField
          label="Title"
          fullWidth
          value={formData.title}
          onChange={handleChange("title")}
        />
        <TextField
          label="Description"
          multiline
          rows={3}
          fullWidth
          value={formData.description}
          onChange={handleChange("description")}
        />
        <Typography variant="subtitle2" fontWeight="bold">
          What You Will Learn
        </Typography>
        {formData.whatYouWillLearn.map((point, index) => (
          <TextField
            key={index}
            fullWidth
            value={point}
            placeholder={`Point ${index + 1}`}
            onChange={(e) => handleLearnChange(index, e.target.value)}
            sx={{ mb: 1 }}
          />
        ))}
        <Button onClick={addLearnPoint}>Add Another</Button>
        <TextField
          label="Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={formData.date}
          onChange={handleChange("date")}
        />
        <TextField
          label="Start Time"
          placeholder="e.g., 3:30 PM"
          fullWidth
          value={formData.startTime}
          onChange={handleChange("startTime")}
        />
        <TextField
          label="End Time"
          placeholder="e.g., 6:00 PM"
          fullWidth
          value={formData.endTime}
          onChange={handleChange("endTime")}
        />
        <TextField
          label="Capacity"
          type="number"
          fullWidth
          value={formData.capacity}
          onChange={handleChange("capacity")}
        />
        <TextField
          label="Minimum Group Size"
          type="number"
          fullWidth
          value={formData.minGroupSize}
          onChange={handleChange("minGroupSize")}
        />
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Saving..." : "Create Session"}
        </Button>
      </Stack>
    </Drawer>
  );
}
